import { useCallback, useEffect, useRef, useState } from "react";
import { storage } from "../utils/storage";

/**
 * Persist a React state value in localStorage.
 * Corrupted / unavailable storage falls back to the initial value without crashing.
 *
 * @param {string} key
 * @param {*} initialValue
 */
export function usePersistedState(key, initialValue) {
  const [value, setValue] = useState(() => {
    if (!key) {
      return typeof initialValue === "function" ? initialValue() : initialValue;
    }
    const fallback =
      typeof initialValue === "function" ? initialValue() : initialValue;
    return storage.get(key, fallback);
  });

  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    if (!key) {
      return;
    }
    storage.set(key, value);
  }, [key, value]);

  const setPersistedValue = useCallback((next) => {
    setValue((prev) =>
      typeof next === "function" ? next(prev) : next
    );
  }, []);

  const reset = useCallback(() => {
    const fallback =
      typeof initialValue === "function" ? initialValue() : initialValue;
    setValue(fallback);
    if (keyRef.current) {
      storage.remove(keyRef.current);
    }
  }, [initialValue]);

  return [value, setPersistedValue, reset];
}

export default usePersistedState;
